import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Image, Text } from 'grommet';
import {
  Search, CloudUpload, Music, BarChart,
} from 'grommet-icons';
import { useAppTheme, ScreenSizeContext } from '@Components/Context';
import { Seo, FullHeightBox, AppButton } from '@Components/Control';
import { localAsset } from '@Helpers/asset';
import { paths } from '@Components/configs';

const FEATURES = [
  {
    icon: Music,
    title: 'YouTube Content ID',
    description: 'Explore your Content ID assets, active claims, and revenue from matched content.',
    number: '01',
  },
  {
    icon: Search,
    title: 'Infringement Detection',
    description: 'See how Redflag finds unauthorized copies of your content across YouTube, TikTok, Facebook, and more.',
    number: '02',
  },
  {
    icon: CloudUpload,
    title: 'Automated Takedowns',
    description: 'Watch our platform automatically issue removal requests and track confirmed takedowns.',
    number: '03',
  },
  {
    icon: BarChart,
    title: 'Review Workflow',
    description: 'See how your team reviews and categorises suspected infringements for maximum accuracy.',
    number: '04',
  },
];

const DemoSplashPage = ({ firstName }) => {
  const navigate = useNavigate();
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const { size } = React.useContext(ScreenSizeContext);
  const small = size ? size.includes('small') : false;

  const usePageBg = isDarkMode ? page.background : page.altBg;
  const logoSrc = isDarkMode
    ? localAsset('images/logo/logo-full-white.svg')
    : localAsset('images/logo/logo-full-color.svg');

  const heroBg = isDarkMode
    ? 'linear-gradient(135deg, #0e0228 0%, #1a0535 60%, #2a0a14 100%)'
    : 'linear-gradient(135deg, #e8f0ff 0%, #f0e8ff 60%, #ffe8ee 100%)';

  return (
    <FullHeightBox fill background={usePageBg} overflow="auto">
      <Seo />
      <Box
        align="center"
        pad={{ horizontal: small ? '1rem' : '2rem', top: '1.5rem', bottom: '2.5rem' }}
      >
        <Box width={{ max: '600px' }} fill="horizontal" gap="0.75rem">

          {/* Hero card */}
          <Box
            round="medium"
            overflow="hidden"
            style={{ background: heroBg, borderBottom: `3px solid ${page.highlight}` }}
          >
            <Box
              pad={{ horizontal: small ? '1.5rem' : '2rem', vertical: '1.5rem' }}
              gap="0.9rem"
            >
              <Box width={small ? '7rem' : '8rem'}>
                <Image src={logoSrc} alt="Redflag AI" fit="contain" />
              </Box>
              <Box gap="0.3rem">
                <Text
                  size={small ? '1.35rem' : '1.6rem'}
                  weight={700}
                  color={page.primaryText}
                  style={{ lineHeight: 1.2 }}
                >
                  {firstName ? `Welcome, ${firstName}.` : 'Welcome to Redflag AI.'}
                </Text>
                <Text size="0.875rem" color={page.primaryText} weight={500}>
                  You&apos;re inside a live demo of the platform.
                </Text>
              </Box>
              <Text
                size="0.8rem"
                color={page.primaryText}
                style={{ lineHeight: 1.6, opacity: 0.85, maxWidth: '440px' }}
              >
                All data shown is sample data — no real content or accounts are involved.
                Explore freely across all sections.
              </Text>
              {/* Demo badge */}
              <Box
                direction="row"
                align="center"
                gap="0.4rem"
                round="xsmall"
                pad={{ vertical: '0.3rem', horizontal: '0.65rem' }}
                style={{
                  display: 'inline-flex',
                  width: 'fit-content',
                  background: isDarkMode ? `${page.highlight}18` : `${page.highlight}12`,
                  border: `1px solid ${page.highlight}35`,
                }}
              >
                <Box
                  round="full"
                  width="0.4rem"
                  height="0.4rem"
                  background={page.highlight}
                  flex={false}
                  style={{ animation: 'rfPulse 2s ease-in-out infinite', flexShrink: 0 }}
                />
                <Text size="0.72rem" color={page.secondaryText} weight={500}>
                  Demo mode · actions are disabled
                </Text>
              </Box>
            </Box>
          </Box>

          {/* Section label */}
          <Box pad={{ horizontal: '0.15rem', top: '0.25rem' }}>
            <Text
              size="0.67rem"
              weight={600}
              color={page.hintText}
              style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}
            >
              What you can explore
            </Text>
          </Box>

          {/* Feature cards */}
          <Box gap="0.45rem">
            {FEATURES.map((feat) => {
              const Icon = feat.icon;
              return (
                <Box
                  key={feat.title}
                  direction="row"
                  gap="0.9rem"
                  pad={{ vertical: '0.75rem', horizontal: '1rem' }}
                  round="small"
                  background={page.tableBg}
                  border={{ color: page.tableBorder, size: '1px', style: 'solid' }}
                  align="center"
                >
                  <Box
                    flex={false}
                    round="small"
                    width="2rem"
                    height="2rem"
                    align="center"
                    justify="center"
                    style={{
                      background: isDarkMode ? `${page.highlight}22` : `${page.highlight}12`,
                      border: `1px solid ${page.highlight}40`,
                      flexShrink: 0,
                    }}
                  >
                    <Icon size="0.9rem" color={page.highlight} />
                  </Box>
                  <Box flex gap="0.15rem">
                    <Text size="0.82rem" weight={600} color={page.tableText}>{feat.title}</Text>
                    <Text size="0.75rem" color={page.secondaryText} style={{ lineHeight: 1.45 }}>
                      {feat.description}
                    </Text>
                  </Box>
                  <Text
                    size="0.95rem"
                    weight={700}
                    color={page.hintText}
                    style={{ opacity: 0.22, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {feat.number}
                  </Text>
                </Box>
              );
            })}
          </Box>

          {/* CTA */}
          <Box align="center" margin={{ top: '0.5rem' }}>
            <AppButton
              overrideHover
              level="dynamicLarge"
              width={small ? '100%' : '13rem'}
              height="2.75rem"
              bgColor={page.highlight}
              color={button.primaryText}
              fontWeight={700}
              label="Start Exploring →"
              onClick={() => navigate(paths.productInfringements)}
            />
          </Box>

        </Box>
      </Box>

      <style>
        {'@keyframes rfPulse { 0%,100%{opacity:1} 50%{opacity:0.35} }'}
      </style>
    </FullHeightBox>
  );
};

DemoSplashPage.propTypes = {
  firstName: PropTypes.string,
};

DemoSplashPage.defaultProps = {
  firstName: '',
};

export default connect((state) => ({ firstName: state.auth?.firstName }))(DemoSplashPage);
